import { useState, useEffect } from 'react';
import { useAuth, UserRole } from '@/lib/auth';
import { listUsers, createUser, updateUserProfile, UserRecord } from '@/lib/userManagement';
import AppHeader from '@/components/AppHeader';

interface UserManagementProps {
  onSignOut: () => void;
}

interface EditState {
  userId: string;
  displayName: string;
  role: UserRole;
  password: string;
}

interface CreateState {
  displayName: string;
  password: string;
  role: UserRole;
}

export default function UserManagement({ onSignOut }: UserManagementProps) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [createState, setCreateState] = useState<CreateState | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listUsers());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (u: UserRecord) => {
    setSaveError(null);
    setEditState({ userId: u.id, displayName: u.displayName, role: u.role, password: '' });
  };

  const openCreate = () => {
    setSaveError(null);
    setCreateState({ displayName: '', password: '', role: 'scorer' });
  };

  const handleCreate = async () => {
    if (!createState) return;
    setSaving(true);
    setSaveError(null);
    try {
      await createUser({
        displayName: createState.displayName.trim(),
        password: createState.password.trim(),
        role: createState.role,
      });
      setCreateState(null);
      await loadUsers();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editState) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updates: Parameters<typeof updateUserProfile>[1] = {};
      const original = users.find(u => u.id === editState.userId);
      if (editState.displayName !== original?.displayName) updates.displayName = editState.displayName;
      if (editState.role !== original?.role) updates.role = editState.role;
      if (editState.password.trim()) updates.password = editState.password.trim();

      if (Object.keys(updates).length === 0) { setEditState(null); return; }

      await updateUserProfile(editState.userId, updates);
      setUsers(prev => prev.map(u =>
        u.id === editState.userId
          ? { ...u, displayName: editState.displayName, role: editState.role }
          : u
      ));
      setEditState(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader onSignOut={onSignOut} title="User Management" navIcon="back" onNavigate={() => history.back()} />

      <div className="flex-1 overflow-y-auto px-4 py-4 safe-area-bottom max-w-md mx-auto w-full">
        {error && (
          <div className="bg-error/20 border border-error rounded-lg p-3 mb-4">
            <p className="text-error text-sm">{error}</p>
            <button onClick={loadUsers} className="text-xs text-error underline mt-1">Retry</button>
          </div>
        )}

        {/* Create User Button */}
        <button
          onClick={openCreate}
          className="w-full card flex items-center gap-3 mb-5 border-dashed hover:border-success/50 hover:bg-success/5 transition-colors active:scale-[0.99]"
        >
          <div className="w-9 h-9 rounded-full bg-success/15 flex items-center justify-center flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-success">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </div>
          <span className="text-sm font-bold text-text-primary">Create New User</span>
        </button>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="card text-center py-10">
            <p className="text-text-muted text-sm">No users found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {users.map((u) => {
              const isSelf = u.id === currentUser?.id;
              return (
                <div key={u.id} className={`card ${isSelf ? 'border-team-a/40 bg-team-a/5' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-text-primary">{u.displayName}</p>
                      {isSelf && <p className="text-xs text-team-a font-semibold">You</p>}
                      <p className="text-xs text-text-muted mt-0.5">
                        Since {new Date(u.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                        u.role === 'admin' ? 'bg-warning/20 text-warning' :
                        u.role === 'viewer' ? 'bg-info/20 text-info' : 'bg-surface-raised text-text-muted'
                      }`}>
                        {u.role.toUpperCase()}
                      </span>
                      {!isSelf && (
                        <button
                          onClick={() => openEdit(u)}
                          className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-raised transition-colors"
                          title="Edit user"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {createState && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl">
            <div className="p-5 border-b border-border">
              <h3 className="text-base font-bold text-text-primary">Create User</h3>
              <p className="text-xs text-text-muted mt-0.5">Add a new scorer, viewer, or admin</p>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5">
                  Username
                </label>
                <input
                  type="text"
                  value={createState.displayName}
                  onChange={e => setCreateState(s => s ? { ...s, displayName: e.target.value } : s)}
                  className="input-field"
                  autoCapitalize="off"
                  autoCorrect="off"
                  placeholder="e.g. Viewer1"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5">
                  Role
                </label>
                <select
                  value={createState.role}
                  onChange={e => setCreateState(s => s ? { ...s, role: e.target.value as UserRole } : s)}
                  className="input-field"
                >
                  <option value="scorer">Scorer</option>
                  <option value="viewer">Viewer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  value={createState.password}
                  onChange={e => setCreateState(s => s ? { ...s, password: e.target.value } : s)}
                  className="input-field"
                  placeholder="Min. 6 characters"
                  autoComplete="new-password"
                />
              </div>

              {saveError && (
                <p className="text-error text-sm">{saveError}</p>
              )}
            </div>

            <div className="p-5 pt-0 grid grid-cols-2 gap-3">
              <button
                onClick={() => { setCreateState(null); setSaveError(null); }}
                disabled={saving}
                className="py-3 rounded-lg font-semibold text-sm bg-surface-raised text-text-primary border border-border transition-all disabled:opacity-50"
              >
                CANCEL
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !createState.displayName.trim() || !createState.password.trim()}
                className="py-3 rounded-lg font-bold text-sm bg-success text-white transition-all disabled:opacity-50"
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    CREATING...
                  </span>
                ) : 'CREATE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editState && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl">
            <div className="p-5 border-b border-border">
              <h3 className="text-base font-bold text-text-primary">Edit User</h3>
              <p className="text-xs text-text-muted mt-0.5">
                {users.find(u => u.id === editState.userId)?.displayName}
              </p>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5">
                  Username
                </label>
                <input
                  type="text"
                  value={editState.displayName}
                  onChange={e => setEditState(s => s ? { ...s, displayName: e.target.value } : s)}
                  className="input-field"
                  autoCapitalize="off"
                  autoCorrect="off"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5">
                  Role
                </label>
                <select
                  value={editState.role}
                  onChange={e => setEditState(s => s ? { ...s, role: e.target.value as UserRole } : s)}
                  className="input-field"
                >
                  <option value="scorer">Scorer</option>
                  <option value="viewer">Viewer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5">
                  New Password <span className="text-text-muted normal-case font-normal">(leave blank to keep)</span>
                </label>
                <input
                  type="password"
                  value={editState.password}
                  onChange={e => setEditState(s => s ? { ...s, password: e.target.value } : s)}
                  className="input-field"
                  placeholder="Min. 6 characters"
                  autoComplete="new-password"
                />
              </div>

              {saveError && (
                <p className="text-error text-sm">{saveError}</p>
              )}
            </div>

            <div className="p-5 pt-0 grid grid-cols-2 gap-3">
              <button
                onClick={() => { setEditState(null); setSaveError(null); }}
                disabled={saving}
                className="py-3 rounded-lg font-semibold text-sm bg-surface-raised text-text-primary border border-border transition-all disabled:opacity-50"
              >
                CANCEL
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editState.displayName.trim()}
                className="py-3 rounded-lg font-bold text-sm bg-team-a text-white transition-all disabled:opacity-50"
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    SAVING...
                  </span>
                ) : 'SAVE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
